import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { BronzeMatchToggle } from '@/components/BronzeMatchToggle'
import { PlayerSelector } from '@/components/PlayerSelector'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Icon, ICONS } from '@/lib/icons'
import { apiClient, handleApiError } from '@/services/api'
import type { GroupStage, KnockoutBracket, Player, Sport } from '@/types/api'
import { resolvePlayerStatsBySport } from '@/lib/player-stats'

interface GroupStageSetupProps {
  seriesId: string
  seriesTitle: string
  seriesSport: Sport
  onCreated: (payload: { stage: GroupStage; bracket: KnockoutBracket }) => void
  clubId?: string
}

const GROUP_COUNT_OPTIONS = [2, 4, 8]
const PLAYERS_PER_GROUP_OPTIONS = [3, 4, 5, 6, 7, 8]
const ADVANCE_PER_GROUP_OPTIONS = [1, 2, 3, 4]

type SeedingMode = 'ZIGZAG' | 'RANDOM' | 'MANUAL'

type SeedingOption = {
  value: SeedingMode
  label: string
  description: string
}

const MAX_OVERFLOW_PER_GROUP = 1

const computeTargetGroupSizes = (totalPlayers: number, groupCount: number): number[] => {
  if (groupCount <= 0) {
    return []
  }

  const baseSize = Math.floor(totalPlayers / groupCount)
  const remainder = totalPlayers % groupCount

  return Array.from({ length: groupCount }, (_, index) => {
    const size = baseSize + (index < remainder ? 1 : 0)
    return size
  })
}

const groupsArrayToRecord = (groups: string[][]): Record<number, string[]> => {
  return groups.reduce<Record<number, string[]>>((acc, group, index) => {
    acc[index] = group
    return acc
  }, {})
}

const findNextGroupWithCapacity = (
  groups: string[][],
  capacities: number[],
  startIndex: number
): number => {
  if (groups.length === 0) {
    return -1
  }

  for (let offset = 0; offset < groups.length; offset++) {
    const index = (startIndex + offset) % groups.length
    if ((groups[index]?.length ?? 0) < (capacities[index] ?? 0)) {
      return index
    }
  }

  return -1
}

const sanitizeManualGroups = (
  selectedPlayers: Player[],
  manualGroups: Record<number, string[]>,
  groupCount: number,
  capacities: number[]
): string[][] => {
  const selectedIds = new Set(selectedPlayers.map(player => player.id))
  const overflow: string[] = []
  const groups = Array.from({ length: groupCount }, (_, index) => {
    const manual = manualGroups[index] ?? []
    const filtered = manual.filter(id => selectedIds.has(id))
    const capacity = capacities[index] ?? filtered.length
    const withinCapacity = filtered.slice(0, capacity)
    const extra = filtered.slice(capacity)
    overflow.push(...extra)
    return withinCapacity
  })

  const assigned = new Set(groups.flat())
  const leftovers = selectedPlayers
    .map(player => player.id)
    .filter(id => !assigned.has(id))
    .concat(overflow)

  for (const playerId of leftovers) {
    const targetIndex = findNextGroupWithCapacity(groups, capacities, 0)
    if (targetIndex === -1) {
      break
    }
    groups[targetIndex].push(playerId)
    assigned.add(playerId)
  }

  return groups
}

const buildZigZagDistribution = (
  players: Player[],
  groupCount: number,
  capacities: number[],
  getPlayerStats: (player: Player) => { winRate: number; totalMatchesPlayed: number }
): string[][] => {
  if (groupCount <= 0) {
    return []
  }

  const groups = Array.from({ length: groupCount }, () => [] as string[])
  const sortedPlayers = [...players].sort((a, b) => {
    const statsA = getPlayerStats(a)
    const statsB = getPlayerStats(b)
    if (statsA.winRate === statsB.winRate) {
      return statsB.totalMatchesPlayed - statsA.totalMatchesPlayed
    }
    return statsB.winRate - statsA.winRate
  })

  for (let index = 0; index < sortedPlayers.length; index++) {
    const round = Math.floor(index / groupCount)
    const isForward = round % 2 === 0
    let groupIndex = isForward
      ? index % groupCount
      : groupCount - 1 - (index % groupCount)

    const step = isForward ? 1 : -1

    let attempts = 0
    while (
      attempts < groupCount &&
      (groups[groupIndex]?.length ?? 0) >= (capacities[groupIndex] ?? 0)
    ) {
      groupIndex = (groupIndex + step + groupCount) % groupCount
      attempts += 1
    }

    if (attempts >= groupCount) {
      const fallback = findNextGroupWithCapacity(groups, capacities, 0)
      if (fallback === -1) {
        break
      }
      groupIndex = fallback
    }

    const player = sortedPlayers[index]
    if (!player) {
      continue
    }

    groups[groupIndex]?.push(player.id)
  }

  return groups
}

const buildRandomDistribution = (
  players: Player[],
  groupCount: number,
  capacities: number[]
): string[][] => {
  if (groupCount <= 0) {
    return []
  }

  const groups = Array.from({ length: groupCount }, () => [] as string[])
  const shuffled = [...players].sort(() => Math.random() - 0.5)

  let startIndex = 0
  for (const player of shuffled) {
    const targetIndex = findNextGroupWithCapacity(groups, capacities, startIndex)
    if (targetIndex === -1) {
      break
    }
    groups[targetIndex]?.push(player.id)
    startIndex = (targetIndex + 1) % groupCount
  }

  return groups
}

export function GroupStageSetup({
  seriesId,
  seriesTitle,
  seriesSport,
  onCreated,
  clubId
}: GroupStageSetupProps) {
  const { t } = useTranslation()
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([])
  const [groupCount, setGroupCount] = useState<number>(4)
  const [playersPerGroup, setPlayersPerGroup] = useState<number>(4)
  const [advancePerGroup, setAdvancePerGroup] = useState<number>(2)
  const [includeBronzeMatch, setIncludeBronzeMatch] = useState<boolean>(true)
  const [loading, setLoading] = useState(false)
  const [seedingMode, setSeedingMode] = useState<SeedingMode>('ZIGZAG')
  const [manualGroups, setManualGroups] = useState<Record<number, string[]>>({})
  const [showPreview, setShowPreview] = useState(false)

  const seedingOptions = useMemo<SeedingOption[]>(
    () => [
      {
        value: 'ZIGZAG',
        label: t('groupStage.setup.seeding.zigzag', 'Zigzag (Win Rate)'),
        description: t(
          'groupStage.setup.seeding.zigzagDescription',
          'Best player → Group 1, 2nd best → Group 2, 3rd best → Group 1, etc.'
        )
      },
      {
        value: 'RANDOM',
        label: t('groupStage.setup.seeding.random', 'Random'),
        description: t(
          'groupStage.setup.seeding.randomDescription',
          'Randomly distribute players across all groups.'
        )
      },
      {
        value: 'MANUAL',
        label: t('groupStage.setup.seeding.manual', 'Manual Assignment'),
        description: t(
          'groupStage.setup.seeding.manualDescription',
          'Manually choose which players go into which group.'
        )
      }
    ],
    [t]
  )

  const getPlayerStats = useCallback((player: Player) => {
    const stats = resolvePlayerStatsBySport(player, seriesSport)
    return {
      winRate: stats?.winRate ?? 0,
      totalMatchesPlayed: stats?.totalMatchesPlayed ?? 0
    }
  }, [seriesSport])

  const totalPlayers = selectedPlayers.length
  const idealPlayers = groupCount * playersPerGroup
  const maxPlayersAllowed = Math.max(
    groupCount * (playersPerGroup + MAX_OVERFLOW_PER_GROUP),
    idealPlayers
  )
  const minPlayersRequired = groupCount * advancePerGroup

  const targetGroupSizes = useMemo(
    () => computeTargetGroupSizes(totalPlayers, groupCount),
    [groupCount, totalPlayers]
  )

  const maxGroupSize = targetGroupSizes.length > 0 ? Math.max(...targetGroupSizes) : 0
  const hasEmptyGroup = targetGroupSizes.some(size => size === 0)
  const emptyGroups = targetGroupSizes.filter(size => size === 0).length
  const exceedsConfiguredMax = maxGroupSize > playersPerGroup + MAX_OVERFLOW_PER_GROUP
  const meetsAdvancementRequirement = targetGroupSizes.every(
    size => size === 0 || size >= advancePerGroup
  )

  const remainingSlots = Math.max(maxPlayersAllowed - totalPlayers, 0)
  const selectionFull = totalPlayers >= maxPlayersAllowed
  const needMoreForAdvance = Math.max(minPlayersRequired - totalPlayers, 0)
  const overLimitCount = Math.max(totalPlayers - maxPlayersAllowed, 0)

  const canSubmit =
    totalPlayers >= minPlayersRequired &&
    !hasEmptyGroup &&
    !exceedsConfiguredMax &&
    meetsAdvancementRequirement &&
    totalPlayers > 0

  // Calculate group distribution based on seeding mode
  const distributedGroups = useMemo(() => {
    if (selectedPlayers.length === 0 || groupCount <= 0) {
      return {}
    }

    const capacities = targetGroupSizes

    if (seedingMode === 'MANUAL') {
      const manualDistribution = sanitizeManualGroups(
        selectedPlayers,
        manualGroups,
        groupCount,
        capacities
      )
      return groupsArrayToRecord(manualDistribution)
    }

    if (seedingMode === 'ZIGZAG') {
      const distribution = buildZigZagDistribution(
        selectedPlayers,
        groupCount,
        capacities,
        getPlayerStats
      )
      return groupsArrayToRecord(distribution)
    }

    if (seedingMode === 'RANDOM') {
      const distribution = buildRandomDistribution(selectedPlayers, groupCount, capacities)
      return groupsArrayToRecord(distribution)
    }

    return {}
  }, [
    seedingMode,
    manualGroups,
    selectedPlayers,
    groupCount,
    getPlayerStats,
    targetGroupSizes
  ])

  // Toggle preview mode
  const handleTogglePreview = useCallback(() => {
    if (!canSubmit) {
      toast.warning(t('groupStage.setup.fillAllSlots', 'Please fill all player slots first'))
      return
    }
    
    const willShowPreview = !showPreview
    
    // If showing preview for the first time in manual mode with empty manual groups,
    // initialize with a default distribution (zigzag)
    if (willShowPreview && seedingMode === 'MANUAL' && Object.keys(manualGroups).length === 0) {
      const manualDistribution = buildZigZagDistribution(
        selectedPlayers,
        groupCount,
        targetGroupSizes,
        getPlayerStats
      )
      setManualGroups(groupsArrayToRecord(manualDistribution))
    }
    
    setShowPreview(willShowPreview)
  }, [
    canSubmit,
    showPreview,
    seedingMode,
    manualGroups,
    groupCount,
    selectedPlayers,
    getPlayerStats,
    targetGroupSizes,
    t
  ])

  // Manual group assignment handlers
  const handleMovePlayerToGroup = useCallback((playerId: string, targetGroupIndex: number) => {
    setManualGroups(prev => {
      const newGroups: Record<number, string[]> = {}
      
      // Initialize all groups
      for (let i = 0; i < groupCount; i++) {
        newGroups[i] = prev[i] ? [...prev[i]] : []
      }
      
      // Remove from current group
      Object.keys(newGroups).forEach(groupIdx => {
        const idx = parseInt(groupIdx, 10)
        newGroups[idx] = newGroups[idx].filter(id => id !== playerId)
      })
      
      // Add to target group (allow overfilling during editing)
      newGroups[targetGroupIndex].push(playerId)
      toast.success(t('groupStage.setup.playerMoved', 'Player moved to Group {{number}}', {
        number: targetGroupIndex + 1
      }))
      
      return newGroups
    })
  }, [groupCount, t])

  // Initialize manual groups when switching to manual mode
  const handleSeedingModeChange = useCallback((value: SeedingMode) => {
    setSeedingMode(value)

    if (value === 'MANUAL' && canSubmit) {
      const manualDistribution = buildZigZagDistribution(
        selectedPlayers,
        groupCount,
        targetGroupSizes,
        getPlayerStats
      )
      setManualGroups(groupsArrayToRecord(manualDistribution))
    }
  }, [canSubmit, selectedPlayers, groupCount, targetGroupSizes, getPlayerStats])

  const handlePlayerSelected = useCallback(
    (player: Player) => {
      const alreadySelected = selectedPlayers.some(p => p.id === player.id)
      if (alreadySelected) {
        toast.info(t('groupStage.setup.playerExists', 'Player already added'))
        return
      }
      if (selectionFull) {
        toast.warning(
          t('groupStage.setup.selectionFull', 'You have filled all available slots')
        )
        return
      }
      setSelectedPlayers(prev => [...prev, player])
    },
    [selectedPlayers, selectionFull, t]
  )

  const selectedPlayerIds = useMemo(
    () => selectedPlayers.map(p => p.id),
    [selectedPlayers]
  )

  const handleRemovePlayer = useCallback((playerId: string) => {
    setSelectedPlayers(prev => prev.filter(player => player.id !== playerId))
  }, [])

  const resetSelection = () => {
    setSelectedPlayers([])
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit) {
      toast.error(t('groupStage.setup.playerCountMismatch', 'Adjust player selection to match configuration'))
      return
    }

    // Validate manual groups if in manual mode
    if (seedingMode === 'MANUAL') {
      const invalidGroups: number[] = []
      for (let i = 0; i < groupCount; i++) {
        const groupSize = (manualGroups[i] || []).length
        if (groupSize !== playersPerGroup) {
          invalidGroups.push(i + 1)
        }
      }
      
      if (invalidGroups.length > 0) {
        toast.error(
          t('groupStage.setup.invalidGroupSizes', 
            'Groups {{groups}} must have exactly {{count}} players each', 
            {
              groups: invalidGroups.join(', '),
              count: playersPerGroup
            }
          )
        )
        return
      }
    }

    try {
      setLoading(true)
      const orderedPlayerIds = Array.from({ length: groupCount })
        .flatMap((_, index) => distributedGroups[index] ?? [])

      if (orderedPlayerIds.length !== selectedPlayers.length) {
        toast.error(
          t(
            'groupStage.setup.distributionMismatch',
            'Unable to distribute players evenly with the current configuration.'
          )
        )
        setLoading(false)
        return
      }

      const response = await apiClient.createGroupStage(seriesId, {
        seriesId,
        playerIds: orderedPlayerIds,
        groupCount,
        playersPerGroup,
        advancePerGroup,
        bronzeMedalMatch: includeBronzeMatch
      })

      toast.success(t('groupStage.setup.created', 'Group stage created'))
      onCreated({ stage: response.groupStage, bracket: response.bracket })
      resetSelection()
      setManualGroups({})
    } catch (error: unknown) {
      handleApiError(error, apiError => {
        toast.error(apiError.message || t('errors.generic'))
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>{t('groupStage.setup.title', 'Create group stage')}</CardTitle>
        <CardDescription>
          {t(
            'groupStage.setup.description',
            'Select players and configure how many groups should compete before the knockout stage for {{title}}.',
            { title: seriesTitle }
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="group-count">{t('groupStage.setup.groupCount', 'Number of groups')}</Label>
              <Select
                value={groupCount.toString()}
                onValueChange={value => {
                  const parsed = Number.parseInt(value, 10)
                  setGroupCount(parsed)
                }}
              >
                <SelectTrigger id="group-count">
                  <SelectValue placeholder={groupCount.toString()} />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_COUNT_OPTIONS.map(option => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="players-per-group">
                {t('groupStage.setup.playersPerGroup', 'Players per group')}
              </Label>
              <Select
                value={playersPerGroup.toString()}
                onValueChange={value => {
                  const parsed = Number.parseInt(value, 10)
                  setPlayersPerGroup(parsed)
                }}
              >
                <SelectTrigger id="players-per-group">
                  <SelectValue placeholder={playersPerGroup.toString()} />
                </SelectTrigger>
                <SelectContent>
                  {PLAYERS_PER_GROUP_OPTIONS.map(option => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="advance-per-group">
                {t('groupStage.setup.advancePerGroup', 'Players advancing from each group')}
              </Label>
              <Select
                value={advancePerGroup.toString()}
                onValueChange={value => {
                  const parsed = Number.parseInt(value, 10)
                  setAdvancePerGroup(parsed)
                }}
              >
                <SelectTrigger id="advance-per-group">
                  <SelectValue placeholder={advancePerGroup.toString()} />
                </SelectTrigger>
                <SelectContent>
                  {ADVANCE_PER_GROUP_OPTIONS.map(option => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Seeding Strategy Selection */}
          <div className="space-y-3 rounded-md border bg-card p-4">
            <Label className="text-sm font-semibold">
              {t('groupStage.setup.seedingStrategy', 'Group Distribution Method')}
            </Label>
            <RadioGroup
              value={seedingMode}
              onValueChange={handleSeedingModeChange}
              className="space-y-3"
            >
              {seedingOptions.map((option) => (
                <div key={option.value} className="flex items-start space-x-3">
                  <RadioGroupItem value={option.value} id={`seeding-${option.value}`} className="mt-0.5" />
                  <div className="flex-1">
                    <Label
                      htmlFor={`seeding-${option.value}`}
                      className="font-medium cursor-pointer"
                    >
                      {option.label}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="flex items-center justify-between rounded-md border bg-muted/50 p-4">
            <div>
              <p className="text-sm font-medium">
                {t('groupStage.setup.playerSelectionSummary', 'Selected players: {{count}}', {
                  count: totalPlayers
                })}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'groupStage.setup.flexibleRange',
                  'Allowed range: {{min}}–{{max}} (ideal {{ideal}})',
                  {
                    min: minPlayersRequired,
                    max: maxPlayersAllowed,
                    ideal: idealPlayers
                  }
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {needMoreForAdvance > 0
                  ? t('groupStage.setup.needMoreForAdvance', 'Add {{count}} more players so {{advance}} can advance.', {
                      count: needMoreForAdvance,
                      advance: advancePerGroup
                    })
                  : exceedsConfiguredMax
                    ? t('groupStage.setup.tooManySelected', 'Remove {{count}} players to stay within the limit.', {
                        count: overLimitCount
                      })
                    : hasEmptyGroup
                      ? t('groupStage.setup.emptyGroupsWarning', 'Assign at least one player to each group.', {
                          count: emptyGroups
                        })
                      : remainingSlots > 0
                        ? t('groupStage.setup.optionalSlots', 'You can optionally add up to {{count}} more players.', {
                            count: remainingSlots
                          })
                        : t('groupStage.setup.readyToStart', 'Player selection ready.')}
              </p>
            </div>
            <BronzeMatchToggle
              id="bronze-switch"
              checked={includeBronzeMatch}
              onCheckedChange={setIncludeBronzeMatch}
              className="max-w-xs"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">
                {t('groupStage.setup.selectedPlayers', 'Selected players')} ({selectedPlayers.length}/{
                  maxPlayersAllowed
                })
              </Label>
              <div className="flex items-center gap-2">
                {selectionFull ? (
                  <Badge variant="secondary">
                    {t('groupStage.setup.selectionFullBadge', 'Selection complete')}
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    {t('groupStage.setup.selectionProgress', '{{count}} remaining', {
                      count: remainingSlots
                    })}
                  </Badge>
                )}
                {selectedPlayers.length > 0 && (
                  <Button type="button" variant="ghost" size="sm" onClick={resetSelection}>
                    <Icon icon={ICONS.delete} width={16} height={16} className="text-destructive" />
                    <span className="ml-1">{t('groupStage.setup.clear', 'Clear')}</span>
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <PlayerSelector
                onPlayerSelected={handlePlayerSelected}
                clubId={clubId}
                excludePlayerIds={selectedPlayerIds}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'groupStage.setup.selectorHint',
                  'Add players one by one. You can adjust the group settings at any time.'
                )}
              </p>
            </div>

            {!showPreview ? (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {selectedPlayers.map(player => {
                  const stats = getPlayerStats(player)
                  const winRate = stats.totalMatchesPlayed > 0
                    ? Math.round(stats.winRate * 100)
                    : null

                  return (
                    <div
                      key={player.id}
                      className="flex items-center justify-between rounded-md border bg-background p-3"
                    >
                      <div>
                        <p className="text-sm font-medium leading-tight">{player.displayName}</p>
                        {winRate !== null ? (
                          <p className="text-xs text-muted-foreground">
                            {t('groupStage.setup.winRate', '{{value}}% win rate', { value: winRate })}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {t('groupStage.setup.noStats', 'No stats yet')}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePlayer(player.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Icon icon={ICONS.close} width={16} height={16} />
                      </Button>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>

          {/* Group Preview */}
          {canSubmit && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">
                  {t('groupStage.setup.groupPreview', 'Group Distribution Preview')}
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleTogglePreview}
                >
                  <Icon 
                    icon={showPreview ? ICONS.arrowUp : ICONS.arrowDown} 
                    width={16} 
                    height={16} 
                  />
                  <span className="ml-1">
                    {showPreview 
                      ? t('groupStage.setup.hidePreview', 'Hide') 
                      : t('groupStage.setup.showPreview', 'Show Preview')
                    }
                  </span>
                </Button>
              </div>

              {showPreview && (
                <div className="space-y-4">
                  {seedingMode === 'MANUAL' && (
                    <div className="rounded-md bg-info-muted dark:bg-info/10 border border-info/30 dark:border-info/40 p-3 text-info-muted-foreground dark:text-info-foreground">
                      <p className="text-sm">
                        <Icon icon={ICONS.info} width={16} height={16} className="inline mr-1" />
                        {t('groupStage.setup.manualHint', 'Click the group numbers (1, 2, 3, 4) to move players between groups.')}
                      </p>
                    </div>
                  )}
                  
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {Array.from({ length: groupCount }).map((_, groupIndex) => {
                      const playerIds = distributedGroups[groupIndex] || []
                      const players = playerIds.map(id => 
                        selectedPlayers.find(p => p.id === id)
                      ).filter(Boolean) as Player[]

                      const targetSize = targetGroupSizes[groupIndex] ?? playersPerGroup
                      const overCapacity = targetSize > 0 && players.length > targetSize
                      const underCapacity = targetSize > 0 && players.length < targetSize
                      const badgeVariant = overCapacity
                        ? 'destructive'
                        : underCapacity
                          ? 'secondary'
                          : 'outline'

                      return (
                        <Card key={groupIndex} className="shadow-sm">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-sm font-semibold">
                                {t('groupStage.setup.groupLabel', 'Group {{number}}', {
                                  number: groupIndex + 1
                                })}
                              </CardTitle>
                              <Badge variant={badgeVariant} className="text-xs">
                                {targetSize > 0
                                  ? `${players.length}/${targetSize}`
                                  : players.length}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {players.map((player, playerIdx) => {
                              const stats = getPlayerStats(player)
                              const winRate = stats.totalMatchesPlayed > 0
                                ? Math.round(stats.winRate * 100)
                                : null

                              return (
                                <div
                                  key={player.id}
                                  className="flex items-center justify-between rounded border bg-muted/30 p-2 hover:bg-muted/50 transition-colors"
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <Badge variant="secondary" className="h-5 w-5 flex-shrink-0 justify-center p-0 text-xs">
                                      {playerIdx + 1}
                                    </Badge>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium truncate">
                                        {player.displayName}
                                      </p>
                                      {winRate !== null ? (
                                        <p className="text-xs text-muted-foreground">
                                          {winRate}% WR
                                        </p>
                                      ) : (
                                        <p className="text-xs text-muted-foreground italic">
                                          {t('groupStage.setup.noStats', 'No stats')}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {seedingMode === 'MANUAL' && (
                                    <div className="flex gap-0.5 ml-2">
                                      {Array.from({ length: groupCount }).map((_, targetIdx) => {
                                        if (targetIdx === groupIndex) return null
                                        return (
                                          <Button
                                            key={targetIdx}
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 text-xs hover:bg-primary hover:text-primary-foreground"
                                            onClick={() => handleMovePlayerToGroup(player.id, targetIdx)}
                                            title={t('groupStage.setup.moveToGroup', 'Move to Group {{number}}', {
                                              number: targetIdx + 1
                                            })}
                                          >
                                            {targetIdx + 1}
                                          </Button>
                                        )
                                      })}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                            {(overCapacity || underCapacity) && (
                              <div
                                className={`rounded border p-3 ${
                                  overCapacity
                                    ? 'border-destructive/50 bg-destructive/10'
                                    : 'border-dashed border-muted-foreground/30 bg-muted/10'
                                }`}
                              >
                                <p
                                  className={`text-xs italic text-center ${
                                    overCapacity
                                      ? 'text-destructive font-medium'
                                      : 'text-muted-foreground'
                                  }`}
                                >
                                  {overCapacity
                                    ? t('groupStage.setup.tooManyPlayers', '{{count}} too many', {
                                        count: players.length - targetSize
                                      })
                                    : t('groupStage.setup.emptySlots', '{{count}} more needed', {
                                        count: targetSize - players.length
                                      })}
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              {t(
                'groupStage.setup.summary',
                'Top {{advance}} players from each group will advance to an {{size}} player knockout bracket.',
                {
                  advance: advancePerGroup,
                  size: groupCount * advancePerGroup
                }
              )}
            </div>
            <Button type="submit" disabled={!canSubmit || loading}>
              {loading
                ? t('groupStage.setup.creating', 'Creating...')
                : t('groupStage.setup.createButton', 'Create group stage')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
